import React from 'react';
import AdvancedSEOHead from "@/components/seo/AdvancedSEOHead";
import { absoluteUrl } from '@/lib/seo';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { CheckCircle, Clock, Target, FileText, BarChart3, Shield } from 'lucide-react';

const IAAudit = () => {
  const seoData = {
    title: "Audit IA Amazon Gratuit — Cosmos Wave | Diagnostic 48-72h",
    description: "Audit IA gratuit pour votre compte Amazon. Diagnostic complet : Ads, SEO, Buy Box, Catalog Health. Livrables détaillés et plan d'action priorisé en 48-72h.",
    breadcrumbs: [
      { name: "Accueil", url: "/" },
      { name: "IA", url: "/ia" },
      { name: "Audit IA Amazon", url: "/ia/audit" }
    ],
    serviceSchema: {
      serviceType: "Audit IA Amazon",
      areaServed: "France",
      description: "Audit gratuit utilisant l'IA pour analyser les performances Amazon : Ads, SEO fiches, Buy Box, Catalog Health. Diagnostic complet en 48-72h."
    },
    faqSchema: [
      {
        question: "Que comprend l'audit IA Amazon gratuit ?",
        answer: "Analyse complète de vos performances Amazon via IA : audit Ads (SP/SB/SD), SEO des fiches produits, santé du catalogue, positionnement Buy Box, et recommandations priorisées."
      },
      {
        question: "Combien de temps dure l'audit IA ?",
        answer: "L'audit est réalisé en 48-72h maximum. Vous recevez un rapport détaillé avec diagnostic IA et plan d'action personnalisé."
      },
      {
        question: "Quels sont les livrables de l'audit ?",
        answer: "Rapport IA complet, analyse des gaps, opportunités identifiées, plan d'action priorisé par ROI potentiel, et recommandations techniques immédiates."
      }
    ]
  };

  return (
    <>
      <AdvancedSEOHead 
        title={seoData.title}
        description={seoData.description}
        breadcrumbs={seoData.breadcrumbs}
        ogImage="/og/ia-audit-1200x630.jpg"
        ogType="article"
        jsonLd={[
          {
            "@context": "https://schema.org",
            "@type": "Service",
            "name": seoData.serviceSchema.serviceType,
            "description": seoData.serviceSchema.description,
            "areaServed": seoData.serviceSchema.areaServed,
            "provider": {
              "@type": "Organization",
              "name": "Cosmos Wave"
            }
          },
          {
            "@context": "https://schema.org", 
            "@type": "FAQPage",
            "mainEntity": seoData.faqSchema.map(faq => ({
              "@type": "Question",
              "name": faq.question,
              "acceptedAnswer": {
                "@type": "Answer",
                "text": faq.answer
              }
            }))
          }
        ]}
      />
      
      <div className="min-h-screen bg-gradient-to-b from-stone-950 via-stone-900 to-stone-950">
        {/* Hero Section */}
        <section className="relative overflow-hidden pt-24 pb-16">
          <div className="absolute inset-0 bg-gradient-to-r from-violet-600/20 via-orange-500/10 to-violet-600/20"></div>
          <div className="relative mx-auto max-w-6xl px-4">
            <div className="text-center">
              <h1 className="text-4xl md:text-6xl font-bold text-white mb-6">
                Audit IA Amazon
                <span className="block text-2xl md:text-4xl text-orange-400 font-normal mt-2">
                  Diagnostic gratuit en 48-72h
                </span>
              </h1>
              <p className="text-xl text-stone-300 max-w-3xl mx-auto mb-8">
                Analyse complète de vos performances Amazon via IA : Ads, SEO fiches, Buy Box, Catalog Health. 
                Recevez un diagnostic précis et un plan d'action priorisé.
              </p>
              <Button size="lg" className="bg-orange-500 hover:bg-orange-600 text-white px-8 py-3">
                Demander l'audit gratuit
              </Button>
            </div>
          </div>
        </section>

        {/* Objectifs Section */}
        <section className="py-16">
          <div className="mx-auto max-w-6xl px-4">
            <h2 className="text-3xl font-bold text-white text-center mb-12">Objectifs de l'audit</h2>
            <div className="grid md:grid-cols-3 gap-8">
              {[
                {
                  icon: <Target className="h-8 w-8" />,
                  title: "Identifier les gaps",
                  description: "Détection automatique des opportunités manquées en Ads, SEO et Catalog Health"
                },
                {
                  icon: <BarChart3 className="h-8 w-8" />,
                  title: "Prioriser les actions",
                  description: "Plan d'action classé par ROI potentiel et effort de mise en œuvre"
                },
                {
                  icon: <Shield className="h-8 w-8" />,
                  title: "Sécuriser le compte",
                  description: "Audit conformité SP-API, risques Buy Box et santé des données"
                }
              ].map((item, index) => (
                <Card key={index} className="p-6 bg-stone-800/50 border-stone-700">
                  <div className="text-orange-400 mb-4">{item.icon}</div>
                  <h3 className="text-xl font-semibold text-white mb-3">{item.title}</h3>
                  <p className="text-stone-300">{item.description}</p>
                </Card>
              ))}
            </div>
          </div>
        </section>

        {/* Livrables Section */}
        <section className="py-16 bg-stone-900/50">
          <div className="mx-auto max-w-6xl px-4">
            <h2 className="text-3xl font-bold text-white text-center mb-12">Livrables détaillés</h2>
            <div className="grid md:grid-cols-2 gap-8">
              {[
                {
                  icon: <FileText className="h-6 w-6" />,
                  title: "Rapport IA complet",
                  items: [
                    "Analyse performance Ads (SP/SB/SD/DSP)",
                    "SEO fiches produits & A+ Content",
                    "Audit Buy Box & Catalog Health",
                    "Conformité SP-API & données"
                  ]
                },
                {
                  icon: <CheckCircle className="h-6 w-6" />,
                  title: "Plan d'action priorisé",
                  items: [
                    "Actions immédiates (quick wins)",
                    "Optimisations moyen terme",
                    "Stratégie long terme",
                    "ROI estimé par action"
                  ]
                }
              ].map((section, index) => (
                <Card key={index} className="p-6 bg-stone-800/50 border-stone-700">
                  <div className="flex items-center gap-3 mb-4">
                    <div className="text-orange-400">{section.icon}</div>
                    <h3 className="text-xl font-semibold text-white">{section.title}</h3>
                  </div>
                  <ul className="space-y-2">
                    {section.items.map((item, itemIndex) => (
                      <li key={itemIndex} className="flex items-start gap-2 text-stone-300">
                        <CheckCircle className="h-4 w-4 text-green-400 mt-1 flex-shrink-0" />
                        {item}
                      </li>
                    ))}
                  </ul>
                </Card>
              ))}
            </div>
          </div>
        </section>

        {/* Délai Section */}
        <section className="py-16">
          <div className="mx-auto max-w-4xl px-4 text-center">
            <div className="flex items-center justify-center gap-4 mb-6">
              <Clock className="h-8 w-8 text-orange-400" />
              <h2 className="text-3xl font-bold text-white">Délai : 48-72h maximum</h2>
            </div>
            <p className="text-xl text-stone-300 mb-8">
              Notre IA analyse vos données Amazon en temps réel. Vous recevez un diagnostic précis 
              avec recommandations actionnables sous 48-72h ouvrées.
            </p>
            <Button size="lg" className="bg-orange-500 hover:bg-orange-600 text-white px-8 py-3">
              Lancer l'audit maintenant
            </Button>
          </div>
        </section>
      </div>
    </>
  );
};

export default IAAudit;